import * as Sentry from "@sentry/browser";
import { getCurrentHostName, isLocalUrl } from "../../utils/uri";
import { _originalLocalStorage } from "../storage/storage.base";
import { DRIVE_STORAGE_PREFIX } from "../../store/storageConstants";

declare let process: {
    env: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        NODE_ENV: string;
    };
};

export declare type TSentryEnvironment = "Development" | "Testing" | "Staging" | "Production" | "Support" | "Mirror";

const BROWSER_TRACING_INTEGRATION = "BrowserTracing";
const BROWSER_PROFILING_INTEGRATION = "BrowserProfiling";
const MAX_BUFFER_SIZE = 100;

export const DSN_GEOTAB_DRIVE_TEST = "https://ccf71943d82d2f72891201e7cfe74cfc@o995408.ingest.us.sentry.io/4507024258498560";
export const DSN_GEOTAB_DRIVE = "https://2eaecaae87134a3c9ad1b4f209a35865@o995408.ingest.sentry.io/4505313798586368";

/**
 * Error report data that ErrorHandler passes to ErrorTracker.
 * Contains all context needed for Sentry error reporting.
 */
export interface IErrorReportData {
    database: string;
    user: { id: string; name: string };
    appVersion?: string | null;
    tag?: string;
    customData?: {
        storage?: object;
        versionInfo?: object;
        browserInfo?: object;
        actions?: string | string[];
    };
    logs?: string[];
    iamLogs?: string[];
}

const isExternalDomainError = (fileName?: string) => {
    if (!fileName) {
        return false;
    }
    try {
        const url = new URL(fileName);
        // Check if the hostname is different from the current page's hostname
        return url.hostname !== window.location.hostname;
    } catch (error) {
        // If filename is not a valid URL, it's likely not external
        return false;
    }
};

/**
 * The page name set in Redux state (mobile.currentPage) when the user is viewing an add-in.
 * Matches the key used in the router's pageModules map.
 */
const DRIVE_ADDIN_PAGE = "ui/addin";

// Sourced from `Geotab.Sentry --> SentryEnvironmentParser`
export const getEnvironmentTag = (): TSentryEnvironment => {
    const stagingDomains = [
        "alpha",
        "preview",
        "bugcrowd",
        "sandbox",
        "mypreview"
    ];

    let currentHostPrefix = getCurrentHostName().toLowerCase();
    if (isLocalUrl(currentHostPrefix)) {
        return "Development";
    }

    currentHostPrefix = currentHostPrefix.split(".")[0];

    if (currentHostPrefix.startsWith("mirror")) {
        return "Mirror";
    }
    if (currentHostPrefix.startsWith("support")) {
        return "Support";
    }
    if (stagingDomains.some(stagingPrefix => currentHostPrefix.startsWith(stagingPrefix))) {
        return "Staging";
    }

    // Check for IP address with HeadlessChrome (testing environment)
    if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(getCurrentHostName()) && navigator.userAgent.includes("HeadlessChrome")) {
        return "Testing";
    }

    return "Production";
};

export const getDsnBasedOnEnvironment = (): string | undefined => {
    const env = getEnvironmentTag();
    switch (env) {
        case "Development":
        case "Testing":
        case "Support":
        case "Mirror":
            return DSN_GEOTAB_DRIVE_TEST;
        case "Staging":
        case "Production":
        default:
            return DSN_GEOTAB_DRIVE;
    }
};

export const getSampleRateForEnvironment = (): number => {
    const environment = getEnvironmentTag();
    switch (environment) {
        case "Development":
        case "Testing":
            return 0.0; // 0% default sampling for development and testing
        case "Staging":
        case "Support":
        case "Mirror":
            return 0.0001; // 0.01% sampling for staging, support, and mirror environments
        case "Production":
        default:
            return 0.0001; // 0.01% sampling for production
    }
};

/**
 * ErrorTracker is a wrapper around Sentry to initialize and configure error tracking and performance monitoring.
 * The actual sending of JS / API errors is still being handled by error.impl.ts.
 * @remarks
 * - Required in `default.ts` as part of the application startup
 * - Make sure to call `initialize()` before using other methods.
 * - Use `configureTracingIntegration()` to enable/disable tracing and profiling based on user consent and feature flags.
 */
export class ErrorTracker {
    /** @internal For test-only use. Use public methods instead of accessing Sentry client directly */
    sentryClient?: ReturnType<typeof Sentry.init>;
    isTracingAllowed = false;
    private pageLoadSpan?: Sentry.Span | undefined;
    private readonly baseOptions: Partial<Sentry.BrowserOptions>;
    private readonly tracingOptions: Partial<Sentry.BrowserOptions>;
    private loggerBuffer: { type: "log" | "error"; message: string }[] = [];
    private versionAttributesProvider: (() => Record<string, any>) | undefined;
    private currentPageProvider: (() => string | undefined) | undefined;

    constructor(customBaseOptions?: Partial<Sentry.BrowserOptions>) {
        this.baseOptions = {
            ...customBaseOptions,
            dsn: getDsnBasedOnEnvironment(),
            defaultIntegrations: false,
            integrations: [
                Sentry.eventFiltersIntegration({ denyUrls: ["localhost", "127.0.0.1", /mytest\d*.geotab.com/] }),
                Sentry.functionToStringIntegration(),
                Sentry.breadcrumbsIntegration({ console: false, xhr: false, fetch: false }),
                Sentry.extraErrorDataIntegration({ depth: 10 }),
                Sentry.linkedErrorsIntegration(),
                Sentry.httpContextIntegration(),
                Sentry.dedupeIntegration()
            ],
            normalizeDepth: 11,
            beforeSend: this.beforeSend.bind(this)
        };

        this.tracingOptions = {
            tracesSampleRate: getSampleRateForEnvironment(),
            tracePropagationTargets: ["localhost"],
            sendDefaultPii: false,
            // Set profilesSampleRate to 1.0 to profile every transaction.
            // Since profilesSampleRate is relative to tracesSampleRate,
            // the final profiling rate can be computed as tracesSampleRate * profilesSampleRate
            // For example, a tracesSampleRate of 0.5 and profilesSampleRate of 0.5 would
            // results in 25% of transactions being profiled (0.5*0.5=0.25)
            profilesSampleRate: 1.0,
            beforeSendTransaction: this.beforeSendTransaction.bind(this)
        };
    }

    /**
     * Configures whether addin errors should be filtered from Sentry.
     * @param shouldFilter A function that returns true if addin errors should be filtered
     */
    setAddinErrorFilter(shouldFilter: () => boolean): void {
        this.shouldFilterAddinErrors = shouldFilter;
    }

    /**
     * Sets a provider that returns the current Drive page name from Redux state
     * (store.getState().mobile.currentPage). Used to detect errors that occur while
     * an add-in is active, even when no add-in frames appear in the stack trace.
     * @param provider A function that returns the current page name, e.g. "ui/addin"
     */
    setCurrentPageProvider(provider: () => string | undefined): void {
        this.currentPageProvider = provider;
    }

    /**
     * Sets a provider function that returns version attributes live from Redux state.
     * Called on every APM transaction to ensure attributes reflect current backend values.
     * @param provider A function that returns the version attributes object
     */
    setVersionAttributesProvider(provider: () => Record<string, any>): void {
        this.versionAttributesProvider = provider;
        this.logger("log", "Version attributes provider configured for APM traces");
    }

    /**
     * Initializes the Sentry error tracking client with configured options and integrations.
     *
     * This method sets up the Sentry client with base error tracking functionality and conditionally
     * enables tracing and profiling features based on configuration. It adds various integrations
     * for enhanced error reporting including event filtering, breadcrumbs, extra error data,
     * and HTTP context capture.
     *
     * @throws {Error} When Sentry client is already initialized
     * @throws {Error} When Sentry initialization fails
     *
     * @remarks
     * - Must be called before any other Sentry operations
     * - Automatically configures tracing integrations if tracing is enabled
     * - Creates an initial page load span for performance monitoring when tracing is active
     * - This method is synchronous and must not be made async
     *
     * @example
     * ```typescript
     * const errorTracker = new ErrorTracker();
     * errorTracker.initialize(); // Sets up Sentry with all configured integrations
     * ```
     */
    initialize() {
        if (this.sentryClient) {
            this.logger("error", "Sentry client is already initialized!");
            throw new Error("Sentry client is already initialized");
        }

        let options = {
            ...this.baseOptions,
            ...this.tracingOptions  // Not enabled by default
        };

        try {
            this.sentryClient = Sentry.init(options);

            if (!this.sentryClient) {
                throw new Error("Sentry initialization failed, client is undefined");
            }

            this.logger("log", `Sentry initialized ${ this.isTracingAllowed ? "with" : "without" } tracing and profiling enabled`);

        } catch (error) {
            this.logger("error", `Sentry initialization failure: ${ error instanceof Error ? error.message : String(error) }`);
        }
    }

    /**
     * Checks if the integrations have actually been enabled
     * @returns boolean
     */
    isTracingActivated() {
        let tracing = this.sentryClient?.getIntegrationByName(BROWSER_TRACING_INTEGRATION);
        let profiling = this.sentryClient?.getIntegrationByName(BROWSER_PROFILING_INTEGRATION);
        return !!tracing && !!profiling;
    }

    async configureTracingIntegration(allowThirdPartyLogging: boolean, isFeatureFlagEnabled: boolean) {
        const isBrowserSupported = this.isBrowserSupportedForTracing();
        const previouslyAllowed = this.isTracingAllowed;
        this.isTracingAllowed = allowThirdPartyLogging && isFeatureFlagEnabled && isBrowserSupported;
        const currentlyHasTracing = this.isTracingActivated();

        if (previouslyAllowed === this.isTracingAllowed) {
            this.logger("log", "Tracing configuration unchanged.");
            return;
        }

        if (!this.sentryClient) {
            this.logger("log", "Sentry client not initialized to enable tracing.");
            return;
        }

        if (!this.isTracingAllowed) {
            this.logger("log", "Tracing disabled per configuration.");
            if (currentlyHasTracing) {
                // Shut down and re-initialize without tracing
                await this.shutdown();
                this.initialize();
            }
            return;
        }

        // Handle enabling tracing
        if (currentlyHasTracing) {
            this.logger("log", "Tracing already enabled.");
            return;
        }

        try {
            this.logger("log", "Enabling tracing integrations...");
            this.sentryClient.addIntegration(Sentry.browserTracingIntegration({
                // Disable automatic span creation - we handle this manually via XHR interception
                instrumentNavigation: false,
                instrumentPageLoad: false,
                shouldCreateSpanForRequest: () => {
                    // Don't create automatic spans for HTTP requests - we handle this manually
                    return false;
                }
            }));
            this.sentryClient.addIntegration(Sentry.browserProfilingIntegration());

            // Start the pageload span for manual routing configuration
            this.pageLoadSpan = Sentry.startBrowserTracingPageLoadSpan(this.sentryClient, {
                op: "pageload",
                name: window.location.pathname,
                attributes: {
                    [Sentry.SEMANTIC_ATTRIBUTE_SENTRY_SOURCE]: "url"
                }
            });

            this.logger("log", "Tracing and profiling integrations enabled.");
        } catch (error) {
            this.logger("error", `Failed to add Sentry integrations: ${ error instanceof Error ? error.message : String(error) }`);
        }
    }

    configureRouting(pageName: string) {
        if (!this.isTracingAllowed || !this.isTracingActivated()) {
            this.logger("log", `Tracing skipped for ${ pageName }; Allowed: ${ this.isTracingAllowed }; Enabled: ${ this.isTracingActivated() }`);
            return;
        }

        // From Sentry documentation: https://docs.sentry.io/platforms/javascript/tracing/instrumentation/automatic-instrumentation/
        // Make sure that the pageload span uses the route name
        // After that, each route change should trigger a navigation span (which will automatically finish the previous one)
        if (this.pageLoadSpan) {
            this.pageLoadSpan?.updateName(pageName);
            this.pageLoadSpan?.setAttribute(
                Sentry.SEMANTIC_ATTRIBUTE_SENTRY_SOURCE,
                "route"
            );
            this.pageLoadSpan = undefined;
        } else {
            if (!this.sentryClient) {
                this.logger("error", `Sentry client not initialized when trying to configure routing`);
                return;
            }
            // If there's a route change before enableTracingAndProfilingIntegrations is called.
            Sentry.startBrowserTracingNavigationSpan(this.sentryClient, {
                op: "navigation",
                name: pageName, // or what the name of the span should be
                attributes: {
                    [Sentry.SEMANTIC_ATTRIBUTE_SENTRY_SOURCE]: "route"
                }
            });
        }
    }

    /**
     * Reports an error to Sentry with the provided context and data.
     * This method configures the Sentry scope and captures the exception.
     * @param error - The error to report
     * @param data - Error report data including database, user, appVersion, tag, custom data, and logs
     */
    reportError(error: Error, data: IErrorReportData): void {
        Sentry.withScope(scope => {
            scope.setLevel("error");
            scope.setTag("database", data.database);
            data.tag && scope.setTag("type", data.tag);
            data.appVersion && scope.setTag("app.release", data.appVersion);

            scope.setUser({
                id: `${ data.database }/${ data.user.id }`,
                username: data.user.name
            });

            if (data.customData) {
                const { storage, versionInfo, browserInfo, actions } = data.customData;
                storage && scope.setContext("Application State", storage as Record<string, unknown>);
                versionInfo && scope.setContext("Versions", versionInfo as Record<string, unknown>);
                browserInfo && scope.setContext("Browser details", browserInfo as Record<string, unknown>);
                const actionsArray = Array.isArray(actions) ? actions : actions ? [actions] : undefined;
                actionsArray?.length && scope.setExtra("Actions", actionsArray);
            }

            data.logs?.length && scope.setExtra("Logs", data.logs);
            data.iamLogs?.length && scope.setExtra("IAM Logs", data.iamLogs);

            Sentry.captureException(error);
        });
    }

    /**
     * Sets a logger function and immediately flushes any buffered log messages to it.
     * @param logger - A function that accepts a log type ("log" or "error") and message string
     */
    setLogger(logger: (type: "log" | "error", message: string) => void) {
        for (const logEntry of this.loggerBuffer) {
            logger(logEntry.type, logEntry.message);
        }
        this.loggerBuffer = [];
        this.logger = logger;
    }

    private shouldFilterAddinErrors: () => boolean = () => false; // Default: don't filter until configured

    /**
     * Gracefully shuts down the Sentry client by flushing pending events and closing the connection.
     * This method performs the following operations:
     * - Flushes any pending Sentry events with no timeout
     * - Closes the Sentry client connection
     * - Clears the Sentry client and page load span references
     * - Logs the shutdown process results or any errors encountered
     * @private
     * @async
     * @returns {Promise<void>} A promise that resolves when the shutdown process is complete
     */
    private async shutdown() {
        const FLUSH_CLOSE_TIMEOUT_MS = 0;
        if (!this.sentryClient) {
            this.logger("log", "Sentry client not initialized, nothing to shut down.");
            return;
        }

        try {
            let flushResult = await this.sentryClient.flush(FLUSH_CLOSE_TIMEOUT_MS);
            let closeResult = await this.sentryClient.close(FLUSH_CLOSE_TIMEOUT_MS);
            this.logger("log", `Sentry flush (${ flushResult }) & shutdown (${ closeResult }) completed`);
        } catch (error) {
            this.logger("error", `Sentry shutdown failure: ${ error instanceof Error ? error.message : String(error) }`);
        }
        this.sentryClient = undefined;

        // Clear the pageLoadSpan reference as it's no longer valid
        this.pageLoadSpan = undefined;
    }

    /**
     * The Sentry `beforeSendTransaction` callback to customize transaction names for JSON-RPC calls.
     * This extracts the method name from span attributes (rpc.method) and uses it as the transaction name
     * instead of the generic /apiv1 URL, making APM traces more meaningful.
     * For ExecuteMultiCall, the span name already contains all the individual methods being called.
     *
     * Also applies version attributes to all transactions for APM tracing.
     */
    private beforeSendTransaction(event: any): any {
        // Apply version attributes to all transactions and their spans (read live from Redux)
        if (this.versionAttributesProvider) {
            const versionAttributes = this.versionAttributesProvider();
            if (Object.keys(versionAttributes).length > 0) {
                // Add version attributes to the transaction
                event.contexts = event.contexts || {};
                event.contexts.Versions = versionAttributes;

                // Also add version attributes to all spans for granular APM data
                if (event.spans && Array.isArray(event.spans)) {
                    event.spans.forEach((span: any) => {
                        span.data = span.data || {};
                        Object.assign(span.data, versionAttributes);
                    });
                }
            }
        }

        // Skip navigation/pageload transactions - only process API transactions
        if (event.op === "navigation" || event.op === "pageload") {
            return event;
        }

        // Update the transaction name if it's a generic /apiv1 call or localhost API call
        if (event.transaction && (event.transaction.includes("/apiv1") || event.transaction.includes(":10001") || event.transaction === "main")) {
            // Try to find the method name from the first matching span
            let methodName: string | null = null;
            if (event.spans) {
                for (const span of event.spans) {
                    if (span.op === "http.client") {
                        // Check for our manual rpc.method attribute in span data
                        methodName = span.data?.["rpc.method"];
                        if (methodName && methodName !== "UNKNOWN") {
                            break;
                        }
                    }
                }
            }
            if (methodName) {
                event.transaction = `API: ${ methodName }`;
            }
        }

        return event;
    }

    /**
     * The Sentry `beforeSend` callback to modify or filter events before sending.
     */
    private beforeSend(event: Sentry.ErrorEvent, _: Sentry.EventHint): Sentry.ErrorEvent | null {
        const exception = event.exception?.values;

        // Ignore unhandled promise rejections & errors without a stack trace
        if (event.tags?.type === "UnhandledPromiseRejection" || (exception && exception[0]?.stacktrace === undefined)) {
            return null;
        }

        // User explicitly denied browser geolocation — not an app error. Filter here (in beforeSend)
        // rather than in error.filter.ts because window.onerror → handleJSImpl bypasses that filter.
        if (exception?.[0]?.value?.includes("GeotabDriveErrors[GeolocationError]") && exception?.[0]?.value?.includes("Permission denied")) {
            return null;
        }

        // Check for external domain errors (including addins)
        let hasTaggedExternalDomainError = false;
        if (exception) {
            exception[0]?.stacktrace?.frames?.forEach(frame => {
                if (!hasTaggedExternalDomainError && isExternalDomainError(frame.filename)) {
                    if (!event.tags) {
                        event.tags = {};
                    }
                    event.tags["external-domain-error"] = true;
                    hasTaggedExternalDomainError = true;
                }
            });
        }

        // If no frame-level signal was found, fall back to the Redux page name.
        // mobile.currentPage is only updated after a page successfully renders, so when an
        // error occurs mid-navigation (e.g. an addin crafting a malformed hash) it still
        // reflects the last successfully loaded page — "ui/addin" means an addin was active.
        if (!hasTaggedExternalDomainError && this.currentPageProvider?.() === DRIVE_ADDIN_PAGE) {
            event.tags = event.tags || {};
            event.tags["external-domain-error"] = true;
            hasTaggedExternalDomainError = true;
        }

        if (!hasTaggedExternalDomainError) {
            event.tags = event.tags || {};
            event.tags["external-domain-error"] = false;
        }

        // Filter out addin errors (external domain errors) if configured to do so
        if (this.shouldFilterAddinErrors() && hasTaggedExternalDomainError) {
            this.logger("log", "Filtering addin error from Sentry (external domain error)");
            return null; // Drop the event
        }

        event.environment = getEnvironmentTag();

        let fullVersion: string[] = [];
        try {
            let storeString = _originalLocalStorage.getItem(`${ DRIVE_STORAGE_PREFIX }@store_data`);
            let storeObject = storeString && JSON.parse(storeString);
            if (storeObject) {
                fullVersion = (storeObject.clientVersion || storeObject.sourceCodeVersion || storeObject.databaseVersion || "").split("-");
            }
        } catch (parseError) {
            // eslint-disable-next-line no-console
            this.logger("error", parseError instanceof Error ? parseError.message : String(parseError));
        }

        if (fullVersion?.length && fullVersion[0]) {
            event.release = `geotabdrive@${ fullVersion[0] }${ (fullVersion[1] ? `+${ fullVersion[1] }` : "") }`;
        }
        return event;
    }

    /**
     * Check for browser support for tracing features
     */
    private isBrowserSupportedForTracing(): boolean {
        try {
            Object["entries"]({});
            // `performance` may be undefined as well in which case we'll throw anyways
            if (!performance.timeOrigin) {
                throw new Error("performance.timeOrigin is undefined");
            }
        } catch (error) {
            return false;
        }
        return true;
    }

    /**
     * Boot logger for this class which writes to console if local
     * and buffers the messages until a proper logger is set.
     * @param type Type of logger message, either a "log" or "error"
     * @param message The message
     */
    private logger = (type: "log" | "error", message: string) => {
        if (isLocalUrl(getCurrentHostName().toLowerCase()) && process.env.NODE_ENV === "development") {
            // eslint-disable-next-line no-console
            console[type](`[ErrorTracker] ${ message }`);
        }
        // Buffer the log message
        if (this.loggerBuffer.length >= MAX_BUFFER_SIZE) {
            this.loggerBuffer.shift(); // Remove oldest entry
        }
        this.loggerBuffer.push({ type, message });
    };
}

